import { MenuItem, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { tariffsApi, type TariffStatus } from "@/api/endpoints";

/**
 * Picks a rate card (W11).
 *
 * <p>In its default shape this is "which card does this price row belong to". Superseded cards are
 * left out: attaching a row to one would look saved and price nothing. Drafts stay in, because
 * building a card's rows before switching it on is the whole point — but they are labelled, so
 * nobody thinks a draft row is live.</p>
 *
 * <p>With <code>includeSuperseded</code> it also serves as the base-card picker a derived rate
 * needs (W13), where the rules are different: what matters is the rate the base card records, not
 * whether that card still prices on its own, so a retired published card is a legitimate base.</p>
 */
export function TariffSelect({
  value, onChange, label = "Rate card", helperText, disabled, size = "small", fullWidth = true,
  blankLabel = "— Standalone —", excludeId, includeSuperseded = false, error = false,
}: {
  value: string;
  onChange: (tariffId: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  /** What the empty choice reads as. The empty option is always offered. */
  blankLabel?: string;
  /** Drops one card from the list — a derived row cannot measure against its own card. */
  excludeId?: string | null;
  /** Include retired cards. Only for picking a base to derive from. */
  includeSuperseded?: boolean;
  error?: boolean;
}) {
  const query = useQuery({
    queryKey: ["tariffs", "options"],
    queryFn: () => tariffsApi.list({ pageSize: 200 }),
    staleTime: 5 * 60_000,
  });

  // Superseded cards price nothing; cards out for approval are frozen and refuse new rows (W16).
  // Both are dropped from the attach list rather than shown and then rejected on save — except the
  // one already on the row being edited, which has to stay visible or the field reads as empty.
  const unattachable: TariffStatus[] = ["Superseded", "Submitted", "Approved"];
  const options = (query.data?.items ?? [])
    .filter(t => includeSuperseded || !unattachable.includes(t.status) || t.id === value)
    .filter(t => !excludeId || t.id !== excludeId);

  return (
    <TextField
      select label={label} size={size} fullWidth={fullWidth} disabled={disabled} error={error}
      value={value} onChange={(e) => onChange(e.target.value)}
      helperText={helperText ?? "Blank = standalone rate, priced directly"}
    >
      <MenuItem value="">{blankLabel}</MenuItem>
      {options.map(t => (
        <MenuItem key={t.id} value={t.id}>
          {t.code}{t.version > 1 ? ` v${t.version}` : ""} — {t.name}
          {t.status !== "Active" && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({t.status})
            </Typography>
          )}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * One-line note explaining why a row on this card is or is not pricing anything. Returns null for
 * a standalone row, where there is nothing to explain.
 */
export function tariffStateNote(
  tariffId?: string | null, status?: TariffStatus | null,
): string | null {
  if (!tariffId) return null;
  switch (status) {
    case "Active": return null;
    case "Draft": return "On a draft card — this rate is not pricing anything yet.";
    case "Submitted": return "On a card out for approval — frozen, and not pricing anything yet.";
    case "Approved": return "On an approved card — signed off, but not pricing until it is activated.";
    case "Superseded": return "On a superseded card — this rate no longer prices anything.";
    default: return "On a rate card that no longer exists — this rate is not pricing anything.";
  }
}
